import { inject, Injectable, signal } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { map } from "rxjs/operators";
import { Router } from "@angular/router";
import { MatSnackBar } from "@angular/material/snack-bar";
import { SnackbarService } from "./snackbar.service";
import { Observable } from "rxjs/internal/Observable";
import { of, throwError } from "rxjs";
import { DataUserResponse, LoginResponse } from "@core/interfaces/auth.interface";
import { UserStateService } from "./user-state.service";
import { RutaDinamicaService } from "@shared/services/ruta-dinamica.service";
import { environment } from "src/environments/environment";

@Injectable({ providedIn: "root" })
export class AuthService {
  private passwordChangeContext = signal<{
    username: string;
    password: string;
    organization: string;
  } | null>(null);
  private rutaDinamicaService = inject(RutaDinamicaService);
  private http = inject(HttpClient);
  public snackBar = inject(MatSnackBar);
  public router = inject(Router);
  private snackbar = inject(SnackbarService);
  private userStateService = inject(UserStateService);

  public login(form: {
    username: string;
    password: string;
    organization: string;
  }): Observable<LoginResponse> {
    // A provisional/expired-password token must never authenticate the next login.
    sessionStorage.removeItem("currentUser");

    return this.http.post<LoginResponse>(
      `${environment.apiUrl}usuarios/org/${form.organization}/auth/validate`,
      {
        username: form.username,
        password: form.password,
      }
    ).pipe(
      map((response) => {
        // Guardar el token automáticamente si viene en la respuesta
        if (response.is_valid === "true" && response.token) {
          sessionStorage.setItem("currentUser", JSON.stringify({ token: response.token }));
        } else {
          sessionStorage.removeItem("currentUser");
        }
        return response;
      })
    );
  }

  public exchangeCode(organization: string, code: string): Observable<LoginResponse> {
    return this.http
      .post<LoginResponse>(`${environment.apiUrl}usuarios/org/${organization}/auth/exchange`, {
        code,
      })
      .pipe(
        map((response) => {
          if (response?.token) {
            sessionStorage.setItem("currentUser", JSON.stringify({ token: response.token }));
          }
          return response;
        })
      );
  }

  public getDataUser(form: {
    username: string;
    password: string;
    organization: string;
  }): Observable<DataUserResponse> {
    if (form.organization) {
      return this.http
        .post<DataUserResponse>(
          `${environment.apiUrl}usuarios/org/${form.organization}/datosusuario`,
          {
            username: form.username,
          }
        )
        .pipe(
          map((user) => {
            if (user) {
              this.setSessionStorage(user, form.organization);
            }
            return user;
          })
        );
    } else {
      this.snackbar.warning("Organización incorrecta", 4000);
      return throwError(() => new Error("Organización incorrecta"));
    }
  }

  private setSessionStorage(user: any, organization: string) {
    sessionStorage.setItem("usuario", user.usuario.toString());
    sessionStorage.setItem("nivelportal", user.nivelportal);
    sessionStorage.setItem("idioma", user.idioma);
    this.userStateService.updateUserInfo({
      usuario: user.usuario,
      rol: user.descnivelportal,
      dusuario: user.dusuario,
      entorno: user.entorno,
      organization: organization,
    });
  }
  public setPasswordChangeContext(context: {
    username: string;
    password: string;
    organization: string;
  }): void {
    this.passwordChangeContext.set(context);
  }

  public getPasswordChangeContext(): {
    username: string;
    password: string;
    organization: string;
  } | null {
    return this.passwordChangeContext();
  }

  public clearPasswordChangeContext(): void {
    this.passwordChangeContext.set(null);
  }

  public clearAuthenticationSession(): void {
    sessionStorage.removeItem("currentUser");
    sessionStorage.removeItem("nivelportal");
    sessionStorage.removeItem("idioma");
    sessionStorage.removeItem("usuario");
    this.userStateService.clearUserState();
  }

  public forgotPassword(
    organization: string,
    email: string,
    username: string
  ): Observable<{ mensaje: string; errortxt: number }> {
    return this.http.post<{ mensaje: string; errortxt: number }>(
      `${environment.apiUrl}usuarios/org/${organization}/auth/olvido`,
      {
        email,
        username,
      }
    );
  }

  public logout() {
    this.rutaDinamicaService.clearMenuCache();
    this.clearAuthenticationSession();
    this.router.navigate(["/login"]);
  }

  public isAuthenticated(): boolean {
    return this.userStateService.isAuthenticated();
  }

  validate(body: any, organization: string): Observable<any> {
    return this.http.post<any>(
      `${environment.apiUrl}usuarios/org/${organization}/auth/cambiopass`,
      body
    );
  }
}
